"use client"

import type React from "react"
import { useEffect } from "react"
import { X, Download } from "lucide-react"

interface ImageModalProps {
  isOpen: boolean
  onClose: () => void
  imageSrc: string
  imageAlt: string
  caption?: string
  projectDate?: string
  children?: React.ReactNode
  className?: string
  showDownload?: boolean
}

export function ImageModal({
  isOpen,
  onClose,
  imageSrc,
  imageAlt,
  caption,
  projectDate,
  children,
  className = "",
  showDownload = true,
}: ImageModalProps) {
  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEsc)
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const response = await fetch(imageSrc, {
        mode: "no-cors",
        cache: "no-cache",
      })

      // If no-cors mode, we can't access the blob, so fallback to proxy approach
      if (response.type === "opaque") {
        const proxyResponse = await fetch(`/api/download-image?url=${encodeURIComponent(imageSrc)}`)
        if (!proxyResponse.ok) {
          throw new Error("Proxy download failed")
        }
        const blob = await proxyResponse.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${caption?.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "project"}.jpg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${caption?.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "project"}.jpg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error("Download failed:", error)
      try {
        const link = document.createElement("a")
        link.href = imageSrc
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (fallbackError) {
        console.error("Fallback failed:", fallbackError)
        alert("Unable to download image. Please right-click and save the image manually.")
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`relative ${className}`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors touch-manipulation"
          aria-label="Close image"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <div className="flex flex-col items-center">
          <img
            src={imageSrc || "/placeholder.svg"}
            alt={imageAlt}
            className="max-w-[95vw] max-h-[calc(95vh-4rem)] object-contain rounded-t-lg shadow-2xl block"
            onClick={(e) => e.stopPropagation()}
          />

          {(caption || projectDate || children) && (
            <div className="w-full bg-black/70 backdrop-blur-sm text-white p-3 md:p-4 rounded-b-lg shadow-2xl">
              <div className="relative flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {caption && <h3 className="font-semibold text-base md:text-lg truncate">{caption}</h3>}
                  {projectDate && <p className="text-sm text-gray-300 mt-1">Created: {projectDate}</p>}
                </div>

                {/* Absolutely centered action buttons */}
                {children && <div className="absolute left-1/2 transform -translate-x-1/2">{children}</div>}

                {showDownload && (
                  <button
                    onClick={handleDownload}
                    className="flex-shrink-0 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors touch-manipulation"
                    aria-label="Download image"
                  >
                    <Download className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
